import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    overdueTasks: 0,
    unreadTickets: 0
  });
  const [projectStats, setProjectStats] = useState({
    planning: 0,
    inProgress: 0,
    completed: 0,
    onHold: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [error, setError] = useState(null);
  const [overduePayments, setOverduePayments] = useState([]);
  const [projectsAtRisk, setProjectsAtRisk] = useState([]);
  const [unresolvedTickets, setUnresolvedTickets] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(null);
  const [pricingStats, setPricingStats] = useState({
    totalValue: 0,
    avgPrice: 0,
    highest: null,
    lowest: null,
    priceBuckets: { low: 0, mid: 0, high: 0 }
  });
  const [allProjects, setAllProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectPayments, setProjectPayments] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchRecentActivities();
    fetchNotifications();
    fetchBusinessHealth();
    fetchPricingStats();
  }, [selectedTimeframe]);

  useEffect(() => {
    async function fetchAllProjects() {
      try {
        const response = await api.get('/admin/projects');
        setAllProjects(response.data.data || []);
      } catch (err) {
        setAllProjects([]);
      }
    }
    fetchAllProjects();
  }, []);

  useEffect(() => {
    async function fetchPaymentsForProject() {
      if (!selectedProjectId) {
        setSelectedProject(null);
        setProjectPayments([]);
        return;
      }
      const project = allProjects.find(p => p.id === selectedProjectId);
      setSelectedProject(project || null);
      if (!project) {
        setProjectPayments([]);
        return;
      }
      try {
        const response = await api.get(`/admin/payments?project=${selectedProjectId}`);
        setProjectPayments(response.data.data || []);
      } catch (err) {
        setProjectPayments([]);
      }
    }
    fetchPaymentsForProject();
  }, [selectedProjectId, allProjects]);

  const fetchDashboardData = async () => {
    try {
      // Fetch all projects to calculate real metrics
      const projectsResponse = await api.get('/admin/projects');
      const projects = projectsResponse.data.data || [];
      
      // Calculate real metrics from projects data
      const totalProjects = projects.length;
      const activeProjects = projects.filter(p => p.status === 'in-progress').length;
      const completedProjects = projects.filter(p => p.status === 'completed').length;
      
      // Calculate total revenue from payments
      const paymentsResponse = await api.get('/admin/payments');
      const payments = paymentsResponse.data.data || [];
      const totalRevenue = payments
        .filter(p => p.status === 'paid' || p.status === 'completed')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      
      setDashboardData({
        totalProjects,
        activeProjects,
        completedProjects,
        totalRevenue,
        pendingPayments: 0,
        overdueTasks: 0,
        unreadTickets: 0
      });
      
      // Calculate project stats by status
      const stats = {
        planning: projects.filter(p => p.status === 'planning').length,
        inProgress: projects.filter(p => p.status === 'in-progress').length,
        completed: projects.filter(p => p.status === 'completed').length,
        onHold: projects.filter(p => p.status === 'on-hold').length
      };
      setProjectStats(stats);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error
      setDashboardData({
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        totalRevenue: 0,
        pendingPayments: 0,
        overdueTasks: 0,
        unreadTickets: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const response = await api.get('/admin/dashboard/activities');
      setRecentActivities(response.data.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.data || []);
    } catch (err) {
      setError('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessHealth = async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      // Fetch all payments, filter for overdue and pending payments
      const paymentsRes = await api.get('/admin/payments');
      const payments = paymentsRes.data.data || [];
      const now = new Date();
      
      const overdue = payments.filter(payment => {
        // Check if payment is overdue based on due_date
        if (payment.due_date) {
          const dueDate = new Date(payment.due_date);
          return dueDate < now && (payment.status || '').toLowerCase() !== 'paid' && (payment.status || '').toLowerCase() !== 'completed';
        }
        // Also check for payments with 'overdue' status
        return (payment.status || '').toLowerCase() === 'overdue';
      });
      setOverduePayments(overdue);

      // Fetch all projects, filter for at risk projects
      const projectsRes = await api.get('/admin/projects');
      const projects = projectsRes.data.data || [];
      
      const atRisk = projects.filter(project => {
        // At risk if status is 'on-hold'
        if ((project.status || '').toLowerCase() === 'on-hold') return true;
        
        // At risk if project has phases and any phase is overdue
        if (Array.isArray(project.phases)) {
          return project.phases.some(phase => {
            if (!phase.end_date || (phase.status || '').toLowerCase() === 'completed') return false;
            const end = new Date(phase.end_date);
            return end < now && (phase.status || '').toLowerCase() !== 'completed';
          });
        }
        
        // At risk if project has a deadline and it's overdue
        if (project.deadline) {
          const deadline = new Date(project.deadline);
          return deadline < now && (project.status || '').toLowerCase() !== 'completed';
        }
        
        return false;
      });
      setProjectsAtRisk(atRisk);

      // Fetch all support tickets, filter for unresolved tickets
      const ticketsRes = await api.get('/admin/support');
      const tickets = ticketsRes.data.data || [];
      
      const unresolved = tickets.filter(ticket => {
        const status = (ticket.status || '').toLowerCase();
        return status !== 'resolved' && status !== 'closed' && status !== 'completed';
      });
      setUnresolvedTickets(unresolved);
      
      console.log('Business Health Data:', {
        overduePayments: overdue.length,
        projectsAtRisk: atRisk.length,
        unresolvedTickets: unresolved.length,
        totalPayments: payments.length,
        totalProjects: projects.length,
        totalTickets: tickets.length
      });
      
    } catch (err) {
      console.error('Error fetching business health data:', err);
      setHealthError('Failed to load business health data.');
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchPricingStats = async () => {
    try {
      const response = await api.get('/admin/projects');
      const projects = response.data.data || [];
      if (!projects.length) {
        setPricingStats({ totalValue: 0, avgPrice: 0, highest: null, lowest: null, priceBuckets: { low: 0, mid: 0, high: 0 } });
        return;
      }
      const prices = projects.map(p => Number(p.budget_estimated) || 0);
      const totalValue = prices.reduce((a, b) => a + b, 0);
      const avgPrice = totalValue / prices.length;
      const highest = projects.reduce((max, p) => (Number(p.budget_estimated) > (max ? Number(max.budget_estimated) : 0) ? p : max), null);
      const lowest = projects.reduce((min, p) => (min === null || Number(p.budget_estimated) < Number(min.budget_estimated) ? p : min), null);
      // Price buckets: <10L, 10L–50L, >50L
      const priceBuckets = { low: 0, mid: 0, high: 0 };
      projects.forEach(p => {
        const val = Number(p.budget_estimated) || 0;
        if (val < 1000000) priceBuckets.low++;
        else if (val < 5000000) priceBuckets.mid++;
        else priceBuckets.high++;
      });
      setPricingStats({ totalValue, avgPrice, highest, lowest, priceBuckets });
    } catch (err) {
      setPricingStats({ totalValue: 0, avgPrice: 0, highest: null, lowest: null, priceBuckets: { low: 0, mid: 0, high: 0 } });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-green-100 text-green-800',
      completed: 'bg-purple-100 text-purple-800',
      'on-hold': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors.planning;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[priority] || colors.low;
  };

  const getAlertIcon = (type) => {
    const icons = {
      payment: '💰',
      task: '⏰',
      support: '🎫'
    };
    return icons[type] || '📢';
  };

  // Calculate payment stats
  const totalPrice = selectedProject ? Number(selectedProject.budget_estimated) || 0 : 0;
  const received = projectPayments.filter(p => (p.status || '').toLowerCase() === 'paid' || (p.status || '').toLowerCase() === 'completed').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const pending = Math.max(totalPrice - received, 0);
  const receivedPercent = totalPrice > 0 ? Math.round((received / totalPrice) * 100) : 0;
  const pendingPercent = totalPrice > 0 ? 100 - receivedPercent : 0;

  // Utility to format numbers in Indian system (e.g., 1 crore 30 lakh)
  function formatIndianPrice(num) {
    if (!num) return 'N/A';
    num = Number(num);
    if (isNaN(num)) return num;
    if (num >= 10000000) {
      const crore = Math.floor(num / 10000000);
      const lakh = Math.floor((num % 10000000) / 100000);
      return `${crore} crore${lakh ? ' ' + lakh + ' lakh' : ''}`;
    } else if (num >= 100000) {
      const lakh = Math.floor(num / 100000);
      const thousand = Math.floor((num % 100000) / 1000);
      return `${lakh} lakh${thousand ? ' ' + thousand + ' thousand' : ''}`;
    } else if (num >= 1000) {
      const thousand = Math.floor(num / 1000);
      return `${thousand} thousand`;
    }
    return num.toString();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6 space-y-6 w-full bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 py-2 sm:py-3">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm">Welcome back! Here's what's happening with your projects.</p>
        </div>
        <div className="flex items-center gap-2 mt-2 sm:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button className="px-4 py-2 bg-palamuru-blue text-white rounded-lg hover:bg-blue-700 transition-colors">
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Total Projects Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 w-full min-w-0 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Projects</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.totalProjects}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <div className="mt-1">
            <span className="text-xs text-gray-500">All time projects</span>
          </div>
        </div>
        {/* Active Projects Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 w-full min-w-0 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Active Projects</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.activeProjects}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          <div className="mt-1">
            <span className="text-xs text-gray-500">Currently in progress</span>
          </div>
        </div>
        {/* Completed Projects Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 w-full min-w-0 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Completed Projects</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData.completedProjects}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-full">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-1">
            <span className="text-xs text-gray-500">Successfully delivered</span>
          </div>
        </div>
        {/* Total Revenue Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 w-full min-w-0 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Revenue</p>
              <div className="flex items-center space-x-1">
                <p className="text-xl sm:text-2xl font-bold text-gray-900">₹{dashboardData.totalRevenue.toLocaleString()}</p>
                <span className="inline-block bg-yellow-100 rounded-full p-1 ml-1 align-middle">
                  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="mt-1">
            <span className="text-xs text-gray-500">Total earned revenue</span>
            <span className="text-xs text-blue-700 ml-2">({formatIndianPrice(dashboardData.totalRevenue)})</span>
          </div>
        </div>
      </div>

      {/* Project Status Overview & Business Health */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 xl:gap-8">
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-soft border border-gray-100 p-3 sm:p-4 overflow-x-auto">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-4">Project Pipeline Flow</h3>
          <div className="relative min-w-[320px] sm:min-w-0">
            {/* Pipeline Flow */}
            <div className="flex flex-wrap justify-between items-center gap-2 sm:gap-4 mb-4 sm:mb-8">
              {/* Planning Stage */}
              <div className="flex flex-col items-center min-w-[80px]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="text-center">
                  <div className="text-base sm:text-lg font-bold text-blue-600">{projectStats.planning}</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-700">Planning</div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex-1 mx-1 sm:mx-2">
                <div className="h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
              </div>

              {/* In Progress Stage */}
              <div className="flex flex-col items-center min-w-[80px]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="text-center">
                  <div className="text-base sm:text-lg font-bold text-green-600">{projectStats.inProgress}</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-700">In Progress</div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex-1 mx-1 sm:mx-2">
                <div className="h-1 bg-gradient-to-r from-green-500 to-purple-500 rounded-full"></div>
              </div>

              {/* Completed Stage */}
              <div className="flex flex-col items-center min-w-[80px]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center">
                  <div className="text-base sm:text-lg font-bold text-purple-600">{dashboardData.completedProjects}</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-700">Completed</div>
                </div>
              </div>
            </div>

            {/* On Hold Section */}
            <div className="border-t border-gray-200 pt-4 sm:pt-6">
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <div className="text-base sm:text-lg font-bold text-yellow-600">{projectStats.onHold}</div>
                    <div className="text-xs sm:text-sm font-medium text-gray-700">On Hold</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600">Success Rate</div>
                  <div className="text-base sm:text-lg font-bold text-green-600">
                    {dashboardData.totalProjects > 0 ? Math.round((dashboardData.completedProjects / dashboardData.totalProjects) * 100) : 0}%
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600">Active Rate</div>
                  <div className="text-base sm:text-lg font-bold text-blue-600">
                    {dashboardData.totalProjects > 0 ? Math.round((dashboardData.activeProjects / dashboardData.totalProjects) * 100) : 0}%
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600">Hold Rate</div>
                  <div className="text-base sm:text-lg font-bold text-yellow-600">
                    {dashboardData.totalProjects > 0 ? Math.round((projectStats.onHold / dashboardData.totalProjects) * 100) : 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Health & Action Center */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Health & Action Center</h3>
          {healthLoading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : healthError ? (
            <div className="text-center text-red-500">{healthError}</div>
          ) : (
          <div className="space-y-6">
            {/* Overdue Payments */}
            <div>
              <h4 className="font-semibold text-red-700 mb-2 flex items-center">💰 Overdue Payments</h4>
              {overduePayments.length === 0 ? (
                <div className="text-gray-500 text-sm">No overdue payments!</div>
              ) : (
                <ul className="space-y-2">
                  {overduePayments.map(payment => (
                    <li key={payment.id} className="flex justify-between items-center bg-red-50 rounded px-3 py-2">
                      <span className="font-medium text-gray-800">{payment.projectName || 'N/A'}</span>
                      <span className="text-gray-600">₹{Number(payment.amount).toLocaleString()}</span>
                      <span className="text-xs text-red-600">Due: {payment.due_date ? new Date(payment.due_date).toLocaleDateString() : '-'}</span>
                      <button className="text-blue-600 hover:underline text-xs ml-2" onClick={() => window.open(`/admin/payments`, '_blank')}>View</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Projects at Risk */}
            <div>
              <h4 className="font-semibold text-yellow-700 mb-2 flex items-center">⚠️ Projects at Risk</h4>
              {projectsAtRisk.length === 0 ? (
                <div className="text-gray-500 text-sm">No projects at risk!</div>
              ) : (
                <ul className="space-y-2">
                  {projectsAtRisk.map(project => (
                    <li key={project.id} className="flex justify-between items-center bg-yellow-50 rounded px-3 py-2">
                      <span className="font-medium text-gray-800">{project.name}</span>
                      <span className="text-xs text-yellow-700">{project.status}</span>
                      <button className="text-blue-600 hover:underline text-xs ml-2" onClick={() => window.open(`/admin/projects/${project.id}`, '_blank')}>View</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Unresolved Support Tickets */}
            <div>
              <h4 className="font-semibold text-purple-700 mb-2 flex items-center">🎫 Unresolved Support Tickets</h4>
              {unresolvedTickets.length === 0 ? (
                <div className="text-gray-500 text-sm">No unresolved tickets!</div>
              ) : (
                <ul className="space-y-2">
                  {unresolvedTickets.map(ticket => (
                    <li key={ticket.id} className="flex justify-between items-center bg-purple-50 rounded px-3 py-2">
                      <span className="font-medium text-gray-800">{ticket.subject || 'No Subject'}</span>
                      <span className="text-xs text-gray-600">Customer: {ticket.customerName || 'N/A'}</span>
                      <button className="text-blue-600 hover:underline text-xs ml-2" onClick={() => window.open(`/admin/support`, '_blank')}>View</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Alerts & Recent Activities */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 xl:gap-8">
        {/* Project Payment Status Visualization */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Payment Status</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Project</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
            >
              <option value="">-- Select a project --</option>
              {allProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.budget_estimated ? `₹${Number(p.budget_estimated).toLocaleString()}` : 'No Price'})</option>
              ))}
            </select>
          </div>
          {selectedProject ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Price</span>
                <span className="font-bold text-gray-900">₹{totalPrice.toLocaleString()} <span className='text-xs text-blue-700 ml-1'>({formatIndianPrice(totalPrice)})</span></span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Received</span>
                <span className="font-bold text-green-700">₹{received.toLocaleString()} <span className='text-xs text-blue-700 ml-1'>({formatIndianPrice(received)})</span></span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Pending</span>
                <span className="font-bold text-yellow-700">₹{pending.toLocaleString()} <span className='text-xs text-blue-700 ml-1'>({formatIndianPrice(pending)})</span></span>
              </div>
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="h-4 rounded-full bg-gradient-to-r from-green-400 to-yellow-400 transition-all duration-500"
                    style={{ width: `${receivedPercent}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Received: {receivedPercent}%</span>
                  <span>Pending: {pendingPercent}%</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-500 text-sm text-center py-8">Select a project to view payment status.</div>
          )}
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
            <Link to="/admin/activities" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-4">
            {recentActivities.slice(0, 5).map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.description}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      {loading ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : (
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <div className="text-gray-500">No notifications found.</div>
          ) : (
            notifications.map((notif, idx) => (
              <div key={notif.id || idx} className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="font-semibold text-yellow-800">{notif.title}</div>
                <div className="text-yellow-700 text-sm">{notif.message}</div>
                <div className="text-xs text-yellow-500 mt-1">{notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 